// manageThemisRuleConditions.ts
import axios, { AxiosResponse } from "axios";
import {
	ThemisConditionOperator,
	ThemisConditionValueType,
} from "./ThemisInterfaces";

type IdResponseDTO = { id: number };

const baseURL = process.env.REACT_APP_API_URL;
const themisAdminBase = `${baseURL}/admin/themis`;
const themisCatalogBase = `${baseURL}/themis/admin/catalog`;

// ----------------------------
// Field Catalog Search DTOs
// ----------------------------

export interface ThemisEnumOptionDto {
  name: string;
  label: string;
  value: number;
}

export interface ThemisCatalogFieldSearchResultRowDto {
  scopeKey: string;
  scopeDisplayName: string;
  fieldKey: string;
  fieldDisplayName: string;
  propertyName?: string | null;
  valueType: number; // backend int enum
  allowedOperators: Array<ThemisConditionOperator | number | string>;
  enumOptions?: ThemisEnumOptionDto[];
  isVirtual: boolean;
}

export interface ThemisCatalogFieldSearchDto {
	rootScopeKey: string;
	targetScopeKey: string;
	phaseKey: string;
	results: ThemisCatalogFieldSearchResultRowDto[];
}

export interface ThemisReachableScopeDto {
  key: string;
  baseScopeKey?: string;
  fields?: ThemisReachableFieldDto[];
}

export interface ThemisReachableFieldDto {
  key: string;
  displayName: string;
  propertyName?: string | null;
  valueType: number;
  allowedOperators: Array<ThemisConditionOperator | number | string>;
  enumOptions?: ThemisEnumOptionDto[];
  isVirtual: boolean;
}

export interface ThemisReachableCatalogDto {
  scopes: ThemisReachableScopeDto[];
}

export function searchFields(params: {
  rootScopeKey: string;
  phaseKey: string;
  q?: string;
  scopeKey?: string;
  valueType?: number;
  page?: number;
  recordsPerPage?: number;
}): Promise<AxiosResponse<ThemisCatalogFieldSearchDto>> {
  return axios.get<ThemisCatalogFieldSearchDto>(`${themisCatalogBase}/fields/search`, {
    params: {
      rootScopeKey: params.rootScopeKey,
      phaseKey: params.phaseKey,
      q: params.q,
      scopeKey: params.scopeKey,
      valueType: params.valueType,
      page: params.page ?? 1,
      recordsPerPage: params.recordsPerPage ?? 20,
    },
  });
}

export function getReachableCatalog(params: {
  rootScopeKey: string;
  phaseKey: string;
}): Promise<AxiosResponse<ThemisReachableCatalogDto>> {
  return axios.get<ThemisReachableCatalogDto>(`${themisCatalogBase}/reachable`, {
    params: {
      rootScopeKey: params.rootScopeKey,
      phaseKey: params.phaseKey,
    },
  });
}

// ----------------------------
// Condition Groups API
// ----------------------------

export type CreateConditionGroupRequestDto = {
	name: string;
	sortOrder: number;
};

export type UpdateConditionGroupRequestDto = {
	name: string;
	sortOrder: number;
};

export function createConditionGroup(
	ruleId: number,
	req: CreateConditionGroupRequestDto
): Promise<AxiosResponse<IdResponseDTO>> {
	return axios.post(`${themisAdminBase}/rules/${ruleId}/condition-groups`, req);
}

export function updateConditionGroup(
	groupId: number,
	req: UpdateConditionGroupRequestDto
): Promise<AxiosResponse<void>> {
	return axios.put(`${themisAdminBase}/condition-groups/${groupId}`, req);
}

export function deleteConditionGroup(
	groupId: number
): Promise<AxiosResponse<void>> {
	return axios.delete(`${themisAdminBase}/condition-groups/${groupId}`);
}

// ----------------------------
// Conditions API
// ----------------------------

const operatorToInt = (op: ThemisConditionOperator): number => {
	switch (op) {
		case ThemisConditionOperator.Equals:
			return 0;
		case ThemisConditionOperator.NotEquals:
			return 1;
		case ThemisConditionOperator.In:
			return 2;
		case ThemisConditionOperator.NotIn:
			return 3;
		case ThemisConditionOperator.GreaterThan:
			return 4;
		case ThemisConditionOperator.GreaterThanOrEqual:
			return 5;
		case ThemisConditionOperator.LessThan:
			return 6;
		case ThemisConditionOperator.LessThanOrEqual:
			return 7;
		case ThemisConditionOperator.Between:
			return 8;
		case ThemisConditionOperator.Contains:
			return 9;
		case ThemisConditionOperator.StartsWith:
			return 10;
		case ThemisConditionOperator.EndsWith:
			return 11;
		case ThemisConditionOperator.IsNull:
			return 12;
		case ThemisConditionOperator.IsNotNull:
			return 13;
		default:
			return 0;
	}
};

const valueTypeToInt = (vt: ThemisConditionValueType): number => {
	switch (vt) {
		case ThemisConditionValueType.String:
			return 0;
		case ThemisConditionValueType.Number:
			return 1;
		case ThemisConditionValueType.Boolean:
			return 2;
		case ThemisConditionValueType.DateTimeUtcMs:
			return 3;
		case ThemisConditionValueType.Int:
			return 4;
		case ThemisConditionValueType.Decimal:
			return 5;
		default:
			return 0;
	}
};

export type CreateConditionRequestDto = {
	fieldKey: string;
	operator: ThemisConditionOperator; // string enum
	valueType: ThemisConditionValueType; // string enum
	sortOrder: number;

	stringValue?: string | null;
	boolValue?: boolean | null;
	longValue?: number | null;
	intValue?: number | null;
	decimalValue?: number | null;
	longValue2?: number | null;
	decimalValue2?: number | null;
	stringListJson?: string | null;
	numberListJson?: string | null;
};

export type UpdateConditionRequestDto = Omit<
	CreateConditionRequestDto,
	"fieldKey"
> & {
	fieldKey?: string;
};

export function createCondition(
	groupId: number,
	req: CreateConditionRequestDto
): Promise<AxiosResponse<IdResponseDTO>> {
	return axios.post(
		`${themisAdminBase}/condition-groups/${groupId}/conditions`,
		{
			...req,
			operator: operatorToInt(req.operator),
			valueType: valueTypeToInt(req.valueType),
		}
	);
}

export function updateCondition(
	id: number,
	req: UpdateConditionRequestDto
): Promise<AxiosResponse<void>> {
	return axios.put(`${themisAdminBase}/conditions/${id}`, {
		...req,
		operator: operatorToInt(req.operator),
		valueType: valueTypeToInt(req.valueType),
	});
}

export function deleteCondition(
	conditionId: number
): Promise<AxiosResponse<void>> {
	return axios.delete(`${themisAdminBase}/conditions/${conditionId}`);
}
